import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, subDays, startOfWeek, startOfMonth, endOfMonth, subMonths, startOfYear, differenceInCalendarDays } from "date-fns";
import { FileText, FileSpreadsheet, FileDown, TrendingUp, TrendingDown, Wallet, Percent, Hash, Search, RotateCcw, ArrowUpRight, ArrowDownRight, Scale } from "lucide-react";
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateField } from "@/components/DateField";
import api, { fmt, fmtDate } from "@/lib/api";

const fd = (d) => format(d, "yyyy-MM-dd");
const PRESETS = [
  { key: "today", label: "Today", range: () => [fd(new Date()), fd(new Date())] },
  { key: "yesterday", label: "Yesterday", range: () => [fd(subDays(new Date(), 1)), fd(subDays(new Date(), 1))] },
  { key: "week", label: "This Week", range: () => [fd(startOfWeek(new Date(), { weekStartsOn: 1 })), fd(new Date())] },
  { key: "month", label: "This Month", range: () => [fd(startOfMonth(new Date())), fd(new Date())] },
  { key: "last-month", label: "Last Month", range: () => [fd(startOfMonth(subMonths(new Date(), 1))), fd(endOfMonth(subMonths(new Date(), 1)))] },
  { key: "year", label: "This Year", range: () => [fd(startOfYear(new Date())), fd(new Date())] },
  { key: "custom", label: "Custom", range: null },
];

const INCOME_TYPES = ["Daily Sales", "Other Income"];
const EXPENSE_TYPES = ["Supplier Payments", "Salary", "Shop Expense", "Miscellaneous"];
const PAY_MODES = ["Cash", "UPI", "Bank", "Card", "Other"];
const KNOWN_MODES = ["Cash", "UPI", "Bank", "Card"];

const DEFAULT_ADV = { txType: "All", incomeType: "All", expenseType: "All", mode: "All", min: "", max: "", q: "" };

const chartTooltip = {
  contentStyle: { background: "hsl(240 6% 10%)", border: "1px solid hsl(240 4% 16%)", borderRadius: 8, fontSize: 12 },
  formatter: (v) => fmt(v),
};

const expenseTypeOf = (cat) => (cat === "Miscellaneous" ? "Miscellaneous" : "Shop Expense");

export default function BAReports() {
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ from: fd(startOfMonth(new Date())), to: fd(new Date()) });
  const [adv, setAdv] = useState(DEFAULT_ADV);
  const [loading, setLoading] = useState("");

  const active = PRESETS.find((p) => p.key === preset);
  const [from, to] = preset === "custom" ? [custom.from, custom.to] : active.range();

  const { data: raw } = useQuery({
    queryKey: ["ba-report-all"],
    queryFn: async () => {
      const [s, e, sup, sal, oi] = await Promise.all([
        api.get("/ba/sales"), api.get("/ba/expenses"), api.get("/ba/supplier-payments"),
        api.get("/ba/salaries"), api.get("/ba/other-income"),
      ]);
      return { sales: s.data, expenses: e.data, suppliers: sup.data, salaries: sal.data, other: oi.data };
    },
  });

  const txns = useMemo(() => {
    if (!raw) return [];
    const t = [];
    raw.sales.forEach((x) => t.push({ id: x.id, date: x.date || "", tx: "Income", cat: "Daily Sales", details: x.notes || "Daily sale", mode: x.payment_mode || "", amount: x.amount || 0 }));
    raw.other.forEach((x) => t.push({ id: x.id, date: x.date || "", tx: "Income", cat: "Other Income", details: [x.source, x.notes].filter(Boolean).join(" · ") || "Other income", mode: x.payment_mode || "", amount: x.amount || 0 }));
    raw.expenses.forEach((x) => t.push({ id: x.id, date: x.date || "", tx: "Expense", cat: expenseTypeOf(x.category), sub: x.category || "", details: [x.category, x.notes].filter(Boolean).join(" · ") || "Expense", mode: x.payment_mode || "", amount: x.amount || 0 }));
    raw.suppliers.filter((x) => x.status === "Paid").forEach((x) => t.push({ id: x.id, date: x.date || "", tx: "Expense", cat: "Supplier Payments", details: [x.supplier_name, x.notes].filter(Boolean).join(" · ") || "Supplier payment", mode: x.payment_mode || "", amount: x.amount || 0 }));
    raw.salaries.filter((x) => x.status === "Paid" && x.paid_date).forEach((x) => t.push({ id: x.id, date: x.paid_date, tx: "Expense", cat: "Salary", details: [x.employee_name, x.month].filter(Boolean).join(" · ") || "Salary", mode: x.payment_mode || "", amount: x.amount || 0 }));
    return t.filter((x) => x.date);
  }, [raw]);

  const filtered = useMemo(() => txns.filter((x) => {
    if (from && x.date < from) return false;
    if (to && x.date > to) return false;
    if (adv.txType !== "All" && x.tx !== adv.txType) return false;
    if (adv.incomeType !== "All" && x.tx === "Income" && x.cat !== adv.incomeType) return false;
    if (adv.incomeType !== "All" && x.tx === "Expense") return false;
    if (adv.expenseType !== "All" && x.tx === "Expense" && x.cat !== adv.expenseType) return false;
    if (adv.expenseType !== "All" && x.tx === "Income") return false;
    if (adv.mode !== "All") {
      if (adv.mode === "Other" ? KNOWN_MODES.includes(x.mode) : x.mode !== adv.mode) return false;
    }
    if (adv.min !== "" && x.amount < Number(adv.min)) return false;
    if (adv.max !== "" && x.amount > Number(adv.max)) return false;
    if (adv.q && !x.details.toLowerCase().includes(adv.q.toLowerCase())) return false;
    return true;
  }), [txns, from, to, adv]);

  const S = useMemo(() => {
    const inc = filtered.filter((x) => x.tx === "Income");
    const exp = filtered.filter((x) => x.tx === "Expense");
    const income = inc.reduce((s, x) => s + x.amount, 0);
    const sales = inc.filter((x) => x.cat === "Daily Sales").reduce((s, x) => s + x.amount, 0);
    const expenses = exp.reduce((s, x) => s + x.amount, 0);
    const days = from && to ? Math.max(differenceInCalendarDays(new Date(to), new Date(from)) + 1, 1) : Math.max(new Set(filtered.map((x) => x.date)).size, 1);
    const sortedInc = [...inc].sort((a, b) => b.amount - a.amount);
    const sortedExp = [...exp].sort((a, b) => b.amount - a.amount);
    return {
      income, sales, expenses, profit: income - expenses,
      margin: income > 0 ? (((income - expenses) / income) * 100).toFixed(1) : "0",
      count: filtered.length, incCount: inc.length, expCount: exp.length,
      avgDailyIncome: income / days, avgDailyExpense: expenses / days,
      highestIncome: sortedInc[0], highestExpense: sortedExp[0],
      avgTxn: filtered.length ? (income + expenses) / filtered.length : 0,
    };
  }, [filtered, from, to]);

  const monthly = useMemo(() => {
    const m = {};
    filtered.forEach((x) => {
      const k = x.date.slice(0, 7);
      m[k] = m[k] || { month: k, income: 0, expense: 0 };
      m[k][x.tx === "Income" ? "income" : "expense"] += x.amount;
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month)).map((r) => ({ ...r, profit: r.income - r.expense }));
  }, [filtered]);

  const trend = useMemo(() => {
    const span = from && to ? differenceInCalendarDays(new Date(to), new Date(from)) : 999;
    const byDay = span <= 62;
    const g = {};
    filtered.forEach((x) => {
      const k = byDay ? x.date : x.date.slice(0, 7);
      g[k] = g[k] || { label: k, income: 0, expense: 0 };
      g[k][x.tx === "Income" ? "income" : "expense"] += x.amount;
    });
    const rows = Object.values(g).sort((a, b) => a.label.localeCompare(b.label));
    let run = 0;
    return rows.map((r) => { run += r.income - r.expense; return { ...r, net: r.income - r.expense, cumulative: run }; });
  }, [filtered, from, to]);

  const incomeBreakdown = useMemo(() => {
    const total = S.income || 1;
    return INCOME_TYPES.map((c) => {
      const v = filtered.filter((x) => x.tx === "Income" && x.cat === c).reduce((s, x) => s + x.amount, 0);
      return { label: c, value: v, pct: S.income > 0 ? ((v / total) * 100).toFixed(1) : "0" };
    });
  }, [filtered, S.income]);

  const expenseBreakdown = useMemo(() => {
    const exp = filtered.filter((x) => x.tx === "Expense");
    const total = S.expenses || 1;
    const val = (fn) => exp.filter(fn).reduce((s, x) => s + x.amount, 0);
    const rows = [
      { label: "Supplier Payments", value: val((x) => x.cat === "Supplier Payments") },
      { label: "Salary", value: val((x) => x.cat === "Salary") },
      { label: "Transport", value: val((x) => x.sub === "Transport") },
      { label: "Miscellaneous", value: val((x) => x.cat === "Miscellaneous") },
      { label: "Shop Expenses", value: val((x) => x.cat === "Shop Expense" && x.sub !== "Transport") },
    ];
    return rows.map((r) => ({ ...r, pct: S.expenses > 0 ? ((r.value / total) * 100).toFixed(1) : "0" }));
  }, [filtered, S.expenses]);

  const hasAdv = JSON.stringify(adv) !== JSON.stringify(DEFAULT_ADV);
  const reset = () => { setAdv(DEFAULT_ADV); setPreset("month"); };

  const doExport = async (fmt2) => {
    setLoading(fmt2);
    try {
      const body = {
        format: fmt2,
        period: `Period: ${fmtDate(from)} to ${fmtDate(to)}${hasAdv ? " (filtered)" : ""}`,
        summary: [
          ["Total Sales", `Rs. ${S.sales.toLocaleString("en-IN")}`],
          ["Total Income", `Rs. ${S.income.toLocaleString("en-IN")}`],
          ["Total Expenses", `Rs. ${S.expenses.toLocaleString("en-IN")}`],
          ["Net Profit", `Rs. ${S.profit.toLocaleString("en-IN")}`],
          ["Profit Margin", `${S.margin}%`],
          ["Total Transactions", String(S.count)],
          ["Avg Daily Income", `Rs. ${Math.round(S.avgDailyIncome).toLocaleString("en-IN")}`],
          ["Avg Daily Expense", `Rs. ${Math.round(S.avgDailyExpense).toLocaleString("en-IN")}`],
        ],
        rows: [...filtered].sort((a, b) => b.date.localeCompare(a.date)).map((x) => ({
          date: x.date, type: x.tx, category: x.cat, details: x.details, mode: x.mode || "—", amount: x.amount,
        })),
      };
      const res = await api.post("/ba/report/export", body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ba-report.${fmt2 === "excel" ? "xlsx" : fmt2}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt2.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); } finally { setLoading(""); }
  };

  const kpis = [
    { label: "Total Sales", value: fmt(S.sales), icon: TrendingUp, accent: "text-teal-400", ring: "from-teal-500/15" },
    { label: "Total Income", value: fmt(S.income), icon: ArrowUpRight, accent: "text-emerald-500", ring: "from-emerald-500/15" },
    { label: "Total Expenses", value: fmt(S.expenses), icon: ArrowDownRight, accent: "text-rose-500", ring: "from-rose-500/15" },
    { label: "Net Profit", value: fmt(S.profit), icon: Wallet, accent: S.profit >= 0 ? "text-amber-500" : "text-red-500", ring: "from-amber-500/15" },
    { label: "Profit Margin", value: `${S.margin}%`, icon: Percent, accent: "text-sky-500", ring: "from-sky-500/15" },
  ];
  const miniKpis = [
    ["Total Transactions", S.count, Hash],
    ["Avg Daily Income", fmt(Math.round(S.avgDailyIncome)), TrendingUp],
    ["Avg Daily Expense", fmt(Math.round(S.avgDailyExpense)), TrendingDown],
  ];

  const tableRows = useMemo(() => [...filtered].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 100), [filtered]);

  return (
    <div className="space-y-6" data-testid="ba-reports-page">
      {/* ---- Filter Bar ---- */}
      <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="ba-reports-filterbar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {PRESETS.map((p) => (
              <button key={p.key} data-testid={`ba-filter-${p.key}`} onClick={() => setPreset(p.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${preset === p.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="ba-export-pdf" className="rounded-lg" disabled={loading === "pdf"} onClick={() => doExport("pdf")}>
              <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <Button variant="outline" size="sm" data-testid="ba-export-excel" className="rounded-lg" disabled={loading === "excel"} onClick={() => doExport("excel")}>
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" data-testid="ba-export-csv" className="rounded-lg" disabled={loading === "csv"} onClick={() => doExport("csv")}>
              <FileDown className="h-3.5 w-3.5 mr-1.5" /> CSV
            </Button>
          </div>
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[160px]"><DateField testId="ba-custom-from" value={custom.from} onChange={(v) => setCustom({ ...custom, from: v })} /></div>
            <span className="text-xs text-muted-foreground">to</span>
            <div className="w-[160px]"><DateField testId="ba-custom-to" value={custom.to} onChange={(v) => setCustom({ ...custom, to: v })} /></div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {[["txType", "Transaction", ["Income", "Expense"]],
            ["incomeType", "Income Type", INCOME_TYPES],
            ["expenseType", "Expense Type", EXPENSE_TYPES],
            ["mode", "Payment Method", PAY_MODES]].map(([key, label, opts]) => (
            <Select key={key} value={adv[key]} onValueChange={(v) => setAdv({ ...adv, [key]: v })}>
              <SelectTrigger data-testid={`ba-adv-${key}`} className="w-[160px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{["All", ...opts].map((o) => <SelectItem key={o} value={o}>{o === "All" ? `All ${label}` : o}</SelectItem>)}</SelectContent>
            </Select>
          ))}
          <Input data-testid="ba-adv-min" type="number" className="w-[110px] h-9" placeholder="Min ₹" value={adv.min} onChange={(e) => setAdv({ ...adv, min: e.target.value })} />
          <Input data-testid="ba-adv-max" type="number" className="w-[110px] h-9" placeholder="Max ₹" value={adv.max} onChange={(e) => setAdv({ ...adv, max: e.target.value })} />
          <div className="relative flex-1 min-w-[170px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input data-testid="ba-adv-search" className="pl-8 h-9" placeholder="Search remarks..." value={adv.q} onChange={(e) => setAdv({ ...adv, q: e.target.value })} />
          </div>
          {(hasAdv || preset !== "month") && (
            <Button variant="ghost" size="sm" data-testid="ba-adv-reset" className="h-9 text-muted-foreground" onClick={reset}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset Filters
            </Button>
          )}
        </div>
      </div>

      {/* ---- KPI Cards ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="ba-kpi-cards">
        {kpis.map(({ label, value, icon: Icon, accent, ring }) => (
          <div key={label} className={`relative overflow-hidden rounded-xl border bg-card p-5 lg:p-6 bg-gradient-to-br ${ring} to-transparent`}>
            <div className="flex items-start justify-between">
              <p className={`font-heading text-2xl lg:text-3xl font-semibold tracking-tight ${accent}`}>{value}</p>
              <Icon className={`h-5 w-5 ${accent} opacity-80`} />
            </div>
            <p className="text-xs lg:text-sm text-muted-foreground mt-2">{label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4" data-testid="ba-mini-kpi-cards">
        {miniKpis.map(([label, value, Icon]) => (
          <div key={label} className="rounded-xl border bg-card p-4 flex items-center justify-between">
            <div>
              <p className="font-heading text-lg font-semibold">{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </div>
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
        ))}
      </div>

      {/* ---- Charts ---- */}
      <div className="rounded-xl border bg-card p-5" data-testid="ba-chart-income-vs-expense">
        <p className="font-heading font-semibold mb-4">Income vs Expense</p>
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={trend}>
            <defs>
              <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} /><stop offset="100%" stopColor="#f43f5e" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 4% 16%)" vertical={false} />
            <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip {...chartTooltip} />
            <Legend />
            <Area type="monotone" dataKey="income" name="Income" stroke="#10b981" strokeWidth={2} fill="url(#gInc)" />
            <Area type="monotone" dataKey="expense" name="Expense" stroke="#f43f5e" strokeWidth={2} fill="url(#gExp)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {[["Monthly Income", "income", "#10b981", "ba-chart-monthly-income"],
          ["Monthly Expense", "expense", "#f43f5e", "ba-chart-monthly-expense"],
          ["Monthly Profit", "profit", "#f59e0b", "ba-chart-monthly-profit"]].map(([title, key, color, tid]) => (
          <div key={key} className="rounded-xl border bg-card p-5" data-testid={tid}>
            <p className="font-heading font-semibold mb-4">{title}</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 4% 16%)" vertical={false} />
                <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip {...chartTooltip} />
                <Bar dataKey={key} fill={color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>

      <div className="rounded-xl border bg-card p-5" data-testid="ba-chart-cashflow">
        <p className="font-heading font-semibold mb-4">Cash Flow Trend <span className="text-xs font-normal text-muted-foreground">(cumulative net for period)</span></p>
        <ResponsiveContainer width="100%" height={230}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 4% 16%)" vertical={false} />
            <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip {...chartTooltip} />
            <Legend />
            <Line type="monotone" dataKey="net" name="Daily Net" stroke="#94a3b8" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
            <Line type="monotone" dataKey="cumulative" name="Cumulative Cash Flow" stroke="#38bdf8" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ---- Breakdowns ---- */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-card p-5" data-testid="ba-income-breakdown">
          <p className="font-heading font-semibold mb-4">Income Breakdown</p>
          <div className="space-y-4">
            {incomeBreakdown.map(({ label, value, pct }) => (
              <div key={label}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span>{label}</span>
                  <span className="text-muted-foreground"><span className="font-medium text-foreground">{fmt(value)}</span> · {pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border bg-card p-5" data-testid="ba-expense-breakdown">
          <p className="font-heading font-semibold mb-4">Expense Breakdown</p>
          <div className="space-y-4">
            {expenseBreakdown.map(({ label, value, pct }) => (
              <div key={label}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span>{label}</span>
                  <span className="text-muted-foreground"><span className="font-medium text-foreground">{fmt(value)}</span> · {pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-rose-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- Transaction Summary ---- */}
      <div>
        <h3 className="font-heading font-semibold text-lg mb-3">Transaction Summary</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ba-txn-summary">
          {[["Income Transactions", S.incCount, ArrowUpRight, "text-emerald-500"],
            ["Expense Transactions", S.expCount, ArrowDownRight, "text-rose-500"],
            ["Highest Income", S.highestIncome ? fmt(S.highestIncome.amount) : "—", TrendingUp, "text-emerald-500", S.highestIncome?.details],
            ["Highest Expense", S.highestExpense ? fmt(S.highestExpense.amount) : "—", TrendingDown, "text-rose-500", S.highestExpense?.details],
            ["Average Transaction", fmt(S.avgTxn), Scale, "text-sky-500"]].map(([label, value, Icon, cls, sub]) => (
            <div key={label} className="rounded-xl border bg-card p-4">
              <div className="flex items-start justify-between">
                <p className={`font-heading text-lg font-semibold ${cls}`}>{value}</p>
                <Icon className={`h-4 w-4 ${cls} opacity-70 shrink-0`} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{label}</p>
              {sub && <p className="text-[11px] text-muted-foreground/70 truncate mt-0.5">{sub}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* ---- Transactions Table ---- */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-lg">Transactions</h3>
          <p className="text-xs text-muted-foreground">{filtered.length} matching{filtered.length > 100 ? " · showing latest 100" : ""}</p>
        </div>
        <div className="rounded-xl border bg-card overflow-x-auto" data-testid="ba-txn-table">
          {tableRows.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground text-center">No transactions match the current filters.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Category</TableHead>
                  <TableHead>Details</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.map((x) => (
                  <TableRow key={`${x.cat}-${x.id}`} data-testid={`ba-txn-row-${x.id}`}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(x.date)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${x.tx === "Income" ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-400"}`}>{x.tx}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{x.cat}</TableCell>
                    <TableCell className="max-w-[280px] truncate">{x.details}</TableCell>
                    <TableCell className="text-muted-foreground">{x.mode || "—"}</TableCell>
                    <TableCell className={`text-right font-medium ${x.tx === "Income" ? "text-emerald-500" : "text-rose-400"}`}>{x.tx === "Income" ? "+" : "−"}{fmt(x.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
